<fgb-mobile-header [titleKey]="'unified.checkout.page.title'" titleDefault="Checkout"></fgb-mobile-header>
<div class="unified-checkout" [class.unified-checkout--true]="isCheckout">
  <div class="unified-checkout__grid">
    <!-- Checkout column -->
    <div class="unified-checkout__main" [@.disabled]="prefersReducedMotion">
      <button type="button" class="back-button" aria-label="Go back" (click)="goBack()">
        <span class="material-icons" aria-hidden="true">arrow_back_ios_new</span>
      </button>

      @if (isLoadingCheckout()) {
        <div class="checkout-loading" role="status" aria-live="polite" aria-busy="true">
          <svg class="spinner" viewBox="0 0 50 50" aria-hidden="true" focusable="false">
            <circle class="spinner__track" cx="25" cy="25" r="20" fill="none" stroke-width="4" />
            <circle class="spinner__arc" cx="25" cy="25" r="20" fill="none" stroke-width="4" stroke-linecap="round" />
          </svg>
        </div>
      } @else {
        <div class="checkout-sections" @staggerChildren>
          @if (isCheckout) {
            <section class="express-checkout">
              <p class="express-checkout__title">
                {{ 'express.checkout.title' | translate: { fgbdefault: 'Express checkout' } }}
              </p>

              <!-- Wallet providers (Apple Pay, Google Pay, PayPal, Paze). The
               component decides which of them can actually render - it drops
               FreedomPay on desktop and skips anything unregistered - and
               lays out however many survive, so there is nothing to gate
               here. It hides itself when none do.

               totalAmount is the server's basket total in minor units, the
               same figure the summary shows, so the wallet sheet quotes what
               the member is about to be charged. -->
              <div class="express-checkout__wallets">
                <fgbcl-express-checkout
                  #wallets
                  class="express-checkout__providers"
                  [totalAmount]="total()"
                  [context]="expressCheckoutContext()"
                ></fgbcl-express-checkout>

                @if (wallets.hasProviders) {
                  <!-- Separates the wallets above from the saved card below:
                   they are alternative ways to pay the same order, not
                   steps. -->
                  <div class="divider">
                    <span class="divider__line"></span>
                    <span class="divider__label">{{ 'express.checkout.or' | translate: { fgbdefault: 'OR' } }}</span>
                    <span class="divider__line"></span>
                  </div>

                  @if (useCardToken()) {
                    <!-- The saved card is its own express route, so it carries the
                   heading again rather than sitting under the wallets'. Gated on the same
                   condition as the row itself, so a frozen card does not leave the heading
                   standing with nothing beneath it. -->
                    <p class="express-checkout__title">
                      {{ 'express.checkout.title' | translate: { fgbdefault: 'Express checkout' } }}
                    </p>
                  }
                }
              </div>

              <!-- Only with a saved card: without a token there is nothing to
               pay with in one press, and the row would be a dead control.
               The wallets above do not depend on it. -->
              @if (useCardToken()) {
                <button
                  type="button"
                  class="linked-card"
                  @fadeInUp
                  [disabled]="isProcessingLinkedCard()"
                  [attr.aria-busy]="isProcessingLinkedCard()"
                  (click)="payWithLinkedCard()"
                >
                  <div class="linked-card__details">
                    <!-- A PayPal account is not a card, so the row carries the provider's
                       own mark. Every other provider stores a real card and is identified
                       by its card type instead. -->
                    @switch (cardInfo()?.ProviderId) {
                      @case (provider.PayPal) {
                        <img
                          class="linked-card__logo"
                          class="card-provider-image card-provider-{{ cardInfo()?.ProviderId }}"
                          src="{{ 'providers/' + cardInfo()?.ProviderId + '.svg' | contentAssetImage }}"
                          [alt]="'payment.card' | translate: { fgbdefault: 'Card' }"
                        />
                      }
                      @default {
                        @switch (cardInfo()?.CardType) {
                          @case (1) {
                            <img
                              class="linked-card__logo card-provider-image card-provider-{{ cardInfo()?.ProviderId }}"
                              [src]="'payment-icons/mastercard.svg' | contentAssetImage"
                              [alt]="'payment.card' | translate: { fgbdefault: 'Card' }"
                            />
                          }
                          @case (2) {
                            <img
                              class="linked-card__logo card-provider-image card-provider-{{ cardInfo()?.ProviderId }}"
                              [src]="'payment-icons/amex.svg' | contentAssetImage"
                              [alt]="'payment.card' | translate: { fgbdefault: 'Card' }"
                            />
                          }
                          @case (3) {
                            <img
                              class="linked-card__logo card-provider-image card-provider-{{ cardInfo()?.ProviderId }}"
                              [src]="'payment-icons/visa.svg' | contentAssetImage"
                              [alt]="'payment.card' | translate: { fgbdefault: 'Card' }"
                            />
                          }
                          @case (4) {
                            <img
                              class="linked-card__logo card-provider-image card-provider-{{ cardInfo()?.ProviderId }}"
                              [src]="'payment-icons/discover.svg' | contentAssetImage"
                              [alt]="'payment.card' | translate: { fgbdefault: 'Card' }"
                            />
                          }
                          @case (5) {
                            <img
                              class="linked-card__logo card-provider-image card-provider-{{ cardInfo()?.ProviderId }}"
                              [src]="'payment-icons/jcb.svg' | contentAssetImage"
                              [alt]="'payment.card' | translate: { fgbdefault: 'Card' }"
                            />
                          }
                          @case (8) {
                            <img
                              class="linked-card__logo card-provider-image card-provider-{{ cardInfo()?.ProviderId }}"
                              [src]="'payment-icons/unionpay.svg' | contentAssetImage"
                              [alt]="'payment.card' | translate: { fgbdefault: 'Card' }"
                            />
                          }
                        }
                      }
                    }
                    <div class="linked-card__text">
                      <p class="linked-card__name">
                        {{ 'payment.card.label' | translate: { fgbdefault: 'Debit/Credit Card' } }}&nbsp;&nbsp;&nbsp;({{
                          cardInfo()?.CardNumber | creditCardNumber: true
                        }})
                      </p>
                      <p class="linked-card__expiry">Exp:{{ cardInfo()?.ExpiryDate | date: 'MM/YY' }}</p>
                    </div>
                  </div>
                  <span class="material-icons linked-card__arrow" aria-hidden="true">arrow_circle_right</span>
                </button>

                <div class="divider">
                  <span class="divider__line"></span>
                  <span class="divider__label">{{ 'express.checkout.or' | translate: { fgbdefault: 'OR' } }}</span>
                  <span class="divider__line"></span>
                </div>
              }
            </section>
          }

          <div class="billing-swap">
            @if (isEditingBillingAddress) {
              <div class="billing-address-editor" @fadeInUp>
                <!-- Billing address -->
                <section class="billing-address">
                  <h1 class="billing-address__title">
                    {{ 'billing.address.title' | translate: { fgbdefault: 'Billing Address' } }}
                  </h1>
                  <p class="billing-address__description">
                    {{
                      'billing.address.description'
                        | translate
                          : {
                              fgbdefault:
                                'Enter your billing address below to ensure your payment details are accurate and up to date.',
                            }
                    }}
                  </p>
                </section>

                <form class="billing-form" [formGroup]="billingForm" (ngSubmit)="continueToPayment()">
                  <fgbcl-model-meta-data-form
                    [formGroup]="billingForm"
                    [models]="addressModels"
                    [wscPrefix]="wscPrefix"
                  ></fgbcl-model-meta-data-form>

                  <button type="submit" class="continue-button" [disabled]="savingAddress">
                    {{ 'billing.address.continue' | translate: { fgbdefault: 'Continue to Payment' } }}
                  </button>
                </form>
              </div>
            } @else {
              <!-- Confirmed billing address -->
              <section class="address-summary" @fadeInUp>
                <div class="address-summary__header">
                  <div class="address-summary__title-group">
                    <span class="material-icons address-summary__icon" aria-hidden="true">check_circle</span>
                    <p class="address-summary__title">
                      {{ 'billing.address.title' | translate: { fgbdefault: 'Billing Address' } }}
                    </p>
                  </div>
                  <button type="button" class="address-summary__edit" (click)="editBillingAddress()">
                    {{ 'billing.address.edit' | translate: { fgbdefault: 'Edit' } }}
                  </button>
                </div>
                <div class="address-summary__details">
                  <p class="address-summary__line">{{ billingAddressLine }}</p>
                  <p class="address-summary__line">{{ billingContactLine }}</p>
                </div>
              </section>

              @if (!showNewCardPayment()) {
                <button
                  type="button"
                  class="pay-button"
                  @fadeInUpDelayed
                  [disabled]="isPaymentFormLoading()"
                  [attr.aria-busy]="isPaymentFormLoading()"
                  (click)="payWithNewCard()"
                >
                  <span class="material-icons pay-button__icon" aria-hidden="true">lock</span>
                  <span class="pay-button__label">{{
                    'payment.pay.new.card' | translate: { fgbdefault: 'PAY WITH NEW CARD' }
                  }}</span>
                </button>
              }
            }
          </div>

          <!-- Kept in the DOM with [hidden] rather than @if, because the provider mounts
             into paymentDiv by getElementById - see loadPaymentForm. That means it never
             gets an :enter event, so the fade is driven by a class applied at the moment
             it is revealed, matching the @fadeInUp used elsewhere on this page. -->
          <div
            class="payment-section"
            [hidden]="isEditingBillingAddress || !showNewCardPayment()"
            [class.payment-section--revealed]="!isEditingBillingAddress && showNewCardPayment()"
          >
            @if (isCheckout) {
              <!-- Payment -->
              <section class="payment-heading">
                <h2 class="payment-heading__title">{{ 'payment.title' | translate: { fgbdefault: 'Payment' } }}</h2>
                <p class="payment-heading__description">
                  {{ 'payment.description' | translate: { fgbdefault: 'All transactions are secure and encrypted' } }}
                </p>
              </section>
            }

            <!-- New card payment panel -->
            <section class="payment-panel">
              <div class="payment-panel__header">
                <p class="payment-panel__header-text">
                  @if (isCheckout) {
                    {{ 'payment.new.card' | translate: { fgbdefault: 'Pay using new card' } }}
                  } @else {
                    {{ 'add.card.form.title' | translate: { fgbdefault: 'Add New Card' } }}
                  }
                </p>
              </div>

              <div class="payment-panel__body">
                <div id="paymentDiv" class="payment-panel__provider"></div>

                @if (isPaymentFormLoading()) {
                  <div class="payment-panel__loading" role="status" aria-live="polite" aria-busy="true">
                    <svg class="spinner" viewBox="0 0 50 50" aria-hidden="true" focusable="false">
                      <circle class="spinner__track" cx="25" cy="25" r="20" fill="none" stroke-width="4" />
                      <circle class="spinner__arc" cx="25" cy="25" r="20" fill="none" stroke-width="4" stroke-linecap="round" />
                    </svg>
                  </div>
                }
              </div>
            </section>
          </div>
        </div>
      }
    </div>

    <!-- Summary column -->
    <aside
      class="unified-checkout__summary"
      [class.unified-checkout__summary--open]="isMobileSummaryOpen()"
      [class.unified-checkout__summary_checkout]="isCheckout"
    >
      <!-- Mobile-only summary bar. On a phone the summary sits above the
           checkout, so it collapses to this row: the label, a chevron and the
           total, which is the part worth seeing at a glance. Hidden on desktop,
           where the summary column is always open and needs no toggle. -->
      <button
        type="button"
        class="summary-bar"
        [attr.aria-expanded]="isMobileSummaryOpen()"
        aria-controls="summary-body"
        (click)="toggleMobileSummary()"
      >
        <span class="summary-bar__label">
          @if (isCheckout) {
            {{ 'summary.title' | translate: { fgbdefault: 'Summary' } }}
          } @else {
            {{ 'add.card.bar.title' | translate: { fgbdefault: 'Why add a card?' } }}
          }
          <span class="material-icons summary-bar__chevron" aria-hidden="true">keyboard_arrow_down</span>
        </span>
        @if (isCheckout) {
          <span class="summary-bar__total">
            @if (isFreeBasket()) {
              {{ 'fgbcl.currency.free' | translate }}
            } @else {
              @if (hasPoints()) {
                {{ totalPoints() | number }} {{ 'fgbcl.currency.pts' | translate }}
              }
              @if (hasPoints() && hasMoneyTotal()) {
                +
              }
              @if (hasMoneyTotal() || !hasPoints()) {
                {{ total() / 100 | FortressCurrencyPipe }}
              }
            }
          </span>
        }
      </button>

      <!-- Collapse wrapper. The animated grid row lives here rather than on the
           aside, which also holds the bar: a row track there would size the bar
           and leave this at full height. -->
      <div class="summary-collapse" id="summary-body">
        <div class="summary">
          @if (!isCheckout) {
            <!-- What linking a card gives the member. Shown in add-card mode in place of
             the order summary, since there is no basket to summarise there. -->
            <section class="card-benefits">
              <div class="card-benefits__item">
                <span class="card-benefits__icon">
                  <span class="material-symbols-outlined material-symbols-outlined--unfilled" aria-hidden="true">mobile_2</span>
                </span>
                <div class="card-benefits__text">
                  <p class="card-benefits__title">
                    {{ 'card.benefits.tap.title' | translate: { fgbdefault: 'Tap to pay at venues' } }}
                  </p>
                  <p class="card-benefits__description">
                    {{
                      'card.benefits.tap.description'
                        | translate
                          : { fgbdefault: 'Use your digital card at concession stands and the Team Store - no wallet needed.' }
                    }}
                  </p>
                </div>
              </div>

              <div class="card-benefits__item">
                <span class="card-benefits__icon">
                  <span class="material-symbols-outlined material-symbols-outlined--unfilled" aria-hidden="true">
                    qr_code_scanner
                  </span>
                </span>
                <div class="card-benefits__text">
                  <p class="card-benefits__title">
                    {{ 'card.benefits.qr.title' | translate: { fgbdefault: 'Scan your QR code' } }}
                  </p>
                  <p class="card-benefits__description">
                    {{
                      'card.benefits.qr.description'
                        | translate: { fgbdefault: 'Earn points automatically on every purchase when your card is linked.' }
                    }}
                  </p>
                </div>
              </div>

              <div class="card-benefits__item">
                <span class="card-benefits__icon">
                  <span class="material-symbols-outlined material-symbols-outlined--unfilled" aria-hidden="true">bolt</span>
                </span>
                <div class="card-benefits__text">
                  <p class="card-benefits__title">
                    {{ 'card.benefits.checkout.title' | translate: { fgbdefault: 'Faster checkout' } }}
                  </p>
                  <p class="card-benefits__description">
                    {{
                      'card.benefits.checkout.description'
                        | translate: { fgbdefault: 'Skip entering card details every time - one tap and you are done.' }
                    }}
                  </p>
                </div>
              </div>
            </section>
          } @else {
            <h2 class="summary__title">{{ 'summary.title' | translate: { fgbdefault: 'Summary' } }}</h2>

            @if (items().length > 0) {
              <div class="summary__items">
                @for (item of items(); track item.id) {
                  <div class="summary-item">
                    <div class="summary-item__image-wrapper">
                      <img class="summary-item__image" [src]="item.imageUrl | contentImage: '6'" [alt]="item.displayName" />
                      <span class="summary-item__quantity">{{ item.quantity }}</span>
                    </div>
                    <div class="summary-item__body">
                      <div class="summary-item__info">
                        <p class="summary-item__name">{{ item.displayName }}</p>
                        <p class="summary-item__source">
                          @switch (item.type) {
                            @case (itemCartType.MARKETPLACE) {
                              {{ 'cart.type.marketplace' | translate: { fgbdefault: 'Marketplace' } }}
                            }
                            @case (itemCartType.PRODUCT) {
                              {{ 'cart.type.product' | translate: { fgbdefault: 'Product' } }}
                            }
                          }
                        </p>
                      </div>
                      <p class="summary-item__price">
                        @switch (item.priceKind) {
                          @case (summaryPriceKind.POINTS) {
                            <span class="summary-item__price-points">
                              {{ item.totalPrice | number: '1.0-0' }}
                              <span class="summary-item__price-unit">{{ 'fgbcl.currency.pts' | translate }}</span>
                            </span>
                          }
                          @case (summaryPriceKind.ECASH) {
                            {{ item.totalPrice / 100 | FortressCurrencyPipe }}
                          }
                          @case (summaryPriceKind.FREE) {
                            {{ 'fgbcl.currency.free' | translate }}
                          }
                          @default {
                            {{ item.totalPrice / 100 | FortressCurrencyPipe }}
                          }
                        }
                      </p>
                    </div>
                  </div>
                }
              </div>
            }

            <div class="summary__totals">
              <hr class="summary__separator" />

              <div class="summary__lines">
                <div class="money-line">
                  <p class="money-line__label">
                    {{ 'summary.subtotal' | translate: { fgbdefault: 'Subtotal' } }} &bull; {{ totalItems() }}
                    @if (totalItems() === 1) {
                      {{ 'summary.item' | translate: { fgbdefault: 'item' } }}
                    } @else {
                      {{ 'summary.items' | translate: { fgbdefault: 'items' } }}
                    }
                  </p>

                  <p class="money-line__value">
                    @if (isFreeBasket()) {
                      {{ 'fgbcl.currency.free' | translate }}
                    } @else {
                      @if (hasPoints()) {
                        {{ totalPoints() | number }} {{ 'fgbcl.currency.pts' | translate }}
                      }
                      @if (hasPoints() && hasMoneySubtotal()) {
                        +
                      }
                      @if (hasMoneySubtotal() || !hasPoints()) {
                        {{ subtotal() / 100 | FortressCurrencyPipe }}
                      }
                    }
                  </p>
                </div>

                @if (estimatedTaxes() > 0) {
                  <div class="money-line">
                    <p class="money-line__label" aria-describedby="estimated-taxes-help">
                      {{ 'summary.estimated.taxes' | translate: { fgbdefault: 'Estimated taxes' } }}

                      <span
                        class="money-line__help"
                        tabindex="-1"
                        [attr.aria-label]="'summary.estimated.taxes.help' | translate: { fgbdefault: 'Why are taxes estimated?' }"
                      >
                        <span class="material-icons money-line__info" aria-hidden="true">info</span>

                        <span class="money-line__tooltip" id="estimated-taxes-help" role="tooltip">
                          {{
                            'summary.estimated.taxes.tooltip'
                              | translate
                                : {
                                    fgbdefault:
                                      'Taxes are calculated from your billing address and the items in your basket. The final amount is confirmed when your payment is processed.',
                                  }
                          }}
                        </span>
                      </span>
                    </p>
                    <p class="money-line__value">{{ estimatedTaxes() / 100 | FortressCurrencyPipe }}</p>
                  </div>
                }
              </div>

              <div class="summary__total">
                <p class="summary__total-label">{{ 'summary.total' | translate: { fgbdefault: 'Total' } }}</p>
                <p class="summary__total-value">
                  @if (isFreeBasket()) {
                    {{ 'fgbcl.currency.free' | translate }}
                  } @else {
                    @if (hasPoints()) {
                      {{ totalPoints() | number }} {{ 'fgbcl.currency.pts' | translate }}
                    }
                    @if (hasPoints() && hasMoneyTotal()) {
                      +
                    }
                    @if (hasMoneyTotal() || !hasPoints()) {
                      {{ total() / 100 | FortressCurrencyPipe }}
                    }
                  }
                </p>
              </div>
            </div>
          }
        </div>
      </div>
    </aside>
  </div>
</div>

@if (isProcessingLinkedCard()) {
  <div
    class="payment-processing"
    role="alertdialog"
    aria-modal="true"
    aria-busy="true"
    aria-labelledby="payment-processing-title"
  >
    <div class="payment-processing__modal">
      <svg class="spinner" viewBox="0 0 50 50" aria-hidden="true" focusable="false">
        <circle class="spinner__track" cx="25" cy="25" r="20" fill="none" stroke-width="4" />
        <circle class="spinner__arc" cx="25" cy="25" r="20" fill="none" stroke-width="4" stroke-linecap="round" />
      </svg>

      <p class="payment-processing__title" id="payment-processing-title">
        {{ 'payment.processing.title' | translate: { fgbdefault: 'Processing your payment' } }}
      </p>
      <p class="payment-processing__description">
        {{ 'payment.processing.description' | translate: { fgbdefault: 'Please wait and do not close this window.' } }}
      </p>
    </div>
  </div>
}
